/**
 * The shapes of the user's UI documents (docs/audits/PILOT-QUOTES-SHELL.md §5.2; customers:
 * ROLLOUT-CUSTOMERS-SHELL.md §5.1; orders: ROLLOUT-ORDERS-SHELL.md §5.1), as S4a's, C4a's and
 * O4's server whitelists them (backend/services/UiPreferencesService.js; no `imported`:
 * DECISION 7).
 *
 * The client checks every document it reads and every one it writes: an unknown key is
 * dropped, a bad value is dropped (so the reader takes its default). It is readGuarded's idea
 * at the server boundary — a newer or hand-edited document can never break a screen, and the
 * CRM never sends a key the server would refuse.
 */

export const SIDEBAR_MODES = ['rail', 'expanded'];
// The list views a user may land on. Stale is not one: it is the follow-ups tab.
export const LIST_VIEWS = ['all', 'draft', 'sent', 'pending_approval', 'won', 'lost', 'expiring',
  'high_value', 'recent', 'no_customer', 'mine'];
export const LIST_LAYOUTS = ['list', 'kanban'];
export const LIST_SORTS = ['date', 'value', 'customer', 'status'];
export const LIST_DIRS = ['asc', 'desc'];
// C4b (ROLLOUT-CUSTOMERS-SHELL.md §5.1): the customers list's views, sorts and directions, as
// C4a's server lists them (CUSTOMER_VIEWS, CUSTOMER_SORTS, CUSTOMER_DIRS).
export const CUSTOMER_LIST_VIEWS = ['all', 'recent', 'open_quotes', 'at_risk', 'unassigned'];
export const CUSTOMER_LIST_SORTS = ['name', 'company', 'city', 'email', 'created'];
export const CUSTOMER_LIST_DIRS = ['asc', 'desc'];
// O4 (ROLLOUT-ORDERS-SHELL.md §5.1): the orders list's views (the server's utils/orderViews.js
// ids, which the route's ORDER_VIEWS also holds), sorts (O1a's whitelist) and directions.
export const ORDER_LIST_VIEWS = ['all', 'open', 'awaiting_delivery', 'awaiting_payment', 'completed', 'cancelled'];
export const ORDER_LIST_SORTS = ['created', 'number', 'total', 'customer', 'delivery'];
export const ORDER_LIST_DIRS = ['asc', 'desc'];
/**
 * G0: the tool ids each module's toolRail may hold, per module (a module's tools are its own;
 * the server whitelists the same lists). Quotes' four are S5's, unchanged; customers' three are
 * C4a's (§5.1, D6); orders' three are O4's (ROLLOUT-ORDERS-SHELL.md §3 D6).
 */
export const TOOL_IDS_BY_MODULE = Object.freeze({
  quotes: Object.freeze(['stock', 'financing', 'customer', 'activity']),
  customers: Object.freeze(['history', 'returns', 'activity']),
  orders: Object.freeze(['sales', 'amendments', 'documents'])
});
/** Quotes' tool ids: the name S5 exported, kept for its callers. */
export const TOOL_IDS = TOOL_IDS_BY_MODULE.quotes;
export const MAX_RECENTS = 10;
const MAX_PANE_PX = 10000;
const MAX_INT = 2147483647;

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const oneOf = (values) => (v) => (values.includes(v) ? v : undefined);
const literal = (value) => (v) => (v === value ? v : undefined);
const bool = (v) => (typeof v === 'boolean' ? v : undefined);
const paneWidth = (v) => (v === null || (Number.isInteger(v) && v > 0 && v <= MAX_PANE_PX) ? v : undefined);
const uniqueOf = (keep, max) => (v) => {
  if (!Array.isArray(v)) return undefined;
  const out = [];
  for (const x of v) if (keep(x) && !out.includes(x)) out.push(x);
  return max ? out.slice(0, max) : out;
};
const recordId = (n) => Number.isInteger(n) && n > 0 && n <= MAX_INT;

const toolRailOf = (module) => uniqueOf((id) => (TOOL_IDS_BY_MODULE[module] || []).includes(id));

const SHAPES = {
  shell: {
    v: literal(1),
    sidebarMode: oneOf(SIDEBAR_MODES),
    // G0: the Sales flyout's pin, a shell setting; since C4b its only home (salesPin.js).
    flyout: { pinned: bool }
  },
  quotes: {
    v: literal(1),
    panes: { listWidth: paneWidth, toolPanelWidth: paneWidth },
    list: {
      defaultView: oneOf(LIST_VIEWS),
      layout: oneOf(LIST_LAYOUTS),
      sort: oneOf(LIST_SORTS),
      dir: oneOf(LIST_DIRS)
    },
    // No flyout since C4b: the pin's pre-G0 home is stripped on read, so a stored one is never
    // sent back (the server still accepts it until its own slice drops it).
    toolRail: toolRailOf('quotes'),
    recents: uniqueOf(recordId, MAX_RECENTS)
  },
  // §5.1 exactly (UiPreferencesService's customers shape). No flyout (the pin is the shell's), no
  // scope (D2), no layout (no pipeline).
  customers: {
    v: literal(1),
    panes: { listWidth: paneWidth, toolPanelWidth: paneWidth },
    list: {
      defaultView: oneOf(CUSTOMER_LIST_VIEWS),
      sort: oneOf(CUSTOMER_LIST_SORTS),
      dir: oneOf(CUSTOMER_LIST_DIRS)
    },
    toolRail: toolRailOf('customers'),
    recents: uniqueOf(recordId, MAX_RECENTS)
  },
  // ROLLOUT-ORDERS-SHELL.md §5.1 exactly (UiPreferencesService's orders shape). No flyout, no
  // scope (D5), no layout.
  orders: {
    v: literal(1),
    panes: { listWidth: paneWidth, toolPanelWidth: paneWidth },
    list: {
      defaultView: oneOf(ORDER_LIST_VIEWS),
      sort: oneOf(ORDER_LIST_SORTS),
      dir: oneOf(ORDER_LIST_DIRS)
    },
    toolRail: toolRailOf('orders'),
    recents: uniqueOf(recordId, MAX_RECENTS)
  }
};

function clean(value, shape) {
  if (!isPlainObject(value)) return undefined;
  const out = {};
  for (const [key, rule] of Object.entries(shape)) {
    if (!(key in value)) continue;
    const v = typeof rule === 'function' ? rule(value[key]) : clean(value[key], rule);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

/** Two sanitised documents hold the same values (key order aside). */
export function sameDoc(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => sameDoc(x, b[i]));
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => k in b && sameDoc(a[k], b[k]));
}

/** The document with only what §5.2 allows; {} for anything that is not an object. */
export function sanitizeDoc(module, doc) {
  const shape = SHAPES[module];
  if (!shape) return {};
  return clean(doc, shape) || {};
}
